/**
 * LgpdRequestDetailDrawer — Drawer com abas para detalhe de LGPDRequest.
 *
 * 4 abas canônicas (Brief 5 B1 design):
 *   1. Dados — titular + tipo + status + workflow
 *   2. Cascade — AuditTreeView com defense-in-depth D-B
 *   3. Documentos — DocumentationGatedUploader evidências
 *   4. Anonimizações — histórico + iniciar Wizard B5
 *
 * Opção A (Capability-driven):
 *   - Aba Cascade: requer audit-events-read + fallback CapabilityLockedBanner
 *   - Botão "Aprovar" / "Rejeitar": requer lgpd-requests-approve
 *   - Botão "Iniciar Anonimização": requer lgpd-anonymization-execute
 *
 * D-B defense-in-depth: cada componente sensível verifica própria capability.
 *
 * P15: dados do titular mascarados na header do drawer (apenas nome + tipo).
 * P17: ações emitem audit server-side (P17 garantido pelo Engine).
 *
 * Reutiliza DS v2.2.0 Drawer + DocumentationGatedUploader.
 */

import { Drawer, DocumentationGatedUploader } from '@lume/ui-components';
import { useState } from 'react';

import { CapabilityGate } from '../../../components/CapabilityGate';
import { useCapability } from '../../../hooks/use-capability';
import { useAnonymizationsByRequest } from '../../../hooks/use-lgpd-anonymization';
import { useTransitionLgpdRequest } from '../../../hooks/use-lgpd-requests';

import { LgpdCascadePreview } from './LgpdCascadePreview';
import { LgpdRequestStatusBadge, REQUEST_TYPE_LABEL } from './LgpdRequestStatusBadge';

import type { LgpdRequestStatus, LgpdRequestWireItem, AnonymizationWireItem } from './types';
import type { DrawerTabItem } from '@lume/ui-components';

// Workflow real (ADR-098, espelha VALID_TRANSITIONS do Engine): os botões de
// ação são derivados daqui, então o FE nunca oferece uma transição inválida.
const VALID_TRANSITIONS: Record<LgpdRequestStatus, LgpdRequestStatus[]> = {
  received: ['identity_validation', 'identity_rejected', 'cancelled_by_requester'],
  identity_validation: ['identity_validated', 'identity_rejected', 'cancelled_by_requester'],
  identity_validated: ['in_processing', 'cancelled_by_requester'],
  identity_rejected: [],
  in_processing: ['pending_information', 'completed', 'rejected_by_law', 'cancelled_by_requester'],
  pending_information: ['in_processing', 'cancelled_by_requester'],
  completed: [],
  rejected_by_law: [],
  cancelled_by_requester: [],
};

// Transições "negativas" exigem motivo (vai em rejectionReason) e estilo danger.
const NEGATIVE_TRANSITIONS: LgpdRequestStatus[] = [
  'identity_rejected',
  'rejected_by_law',
  'cancelled_by_requester',
];

// Rótulo PT do verbo de cada transição (texto do botão).
const TRANSITION_LABEL: Record<LgpdRequestStatus, string> = {
  received: 'Recebida',
  identity_validation: 'Iniciar validação de identidade',
  identity_validated: 'Confirmar identidade',
  identity_rejected: 'Rejeitar identidade',
  in_processing: 'Iniciar processamento',
  pending_information: 'Solicitar informação ao titular',
  completed: 'Concluir solicitação',
  rejected_by_law: 'Rejeitar (base legal)',
  cancelled_by_requester: 'Cancelar (titular)',
};

interface LgpdRequestDetailDrawerProps {
  request: LgpdRequestWireItem | null;
  onClose: () => void;
  onInitiateAnonymization: (request: LgpdRequestWireItem) => void;
}

export function LgpdRequestDetailDrawer({
  request,
  onClose,
  onInitiateAnonymization,
}: LgpdRequestDetailDrawerProps): JSX.Element {
  const canApprove = useCapability('lgpd-requests-approve');
  const canExecute = useCapability('lgpd-anonymization-execute');

  const transitionMutation = useTransitionLgpdRequest();

  const { data: anonymizations, isLoading: anonLoading } = useAnonymizationsByRequest(
    request?.id ?? null,
  );

  // Transição negativa pendente (qual estado-alvo) + motivo obrigatório.
  const [pendingNegative, setPendingNegative] = useState<LgpdRequestStatus | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  if (request === null) return <></>;

  // ─── Tab 1: Dados ─────────────────────────────────────────────────────────

  const tabDados: DrawerTabItem = {
    id: 'dados',
    label: 'Dados',
    content: (
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Titular */}
        <Section title="Titular">
          <InfoRow label="Nome" value={request.requesterSelfDeclaredData?.name ?? '—'} />
          <InfoRow
            label="CPF"
            value={
              request.requesterSelfDeclaredData?.cpf
                ? `***.***.***-${request.requesterSelfDeclaredData.cpf.slice(-2)}`
                : '—'
            }
          />
          <InfoRow label="Email" value={request.requesterSelfDeclaredData?.email ?? '—'} />
          <InfoRow label="Telefone" value={request.requesterSelfDeclaredData?.phone ?? '—'} />
        </Section>

        {/* Solicitação */}
        <Section title="Solicitação">
          <InfoRow
            label="Tipo"
            value={REQUEST_TYPE_LABEL[request.requestType] ?? request.requestType}
          />
          <InfoRow label="Status">
            <LgpdRequestStatusBadge status={request.status} size="md" />
          </InfoRow>
          <InfoRow
            label="Prazo (art. 19)"
            value={`${new Date(request.legalDeadlineAt).toLocaleDateString('pt-BR')} · ${request.daysUntilDeadline}d`}
          />
          {Object.keys(request.requestDetails).length > 0 && (
            <InfoRow
              label="Detalhes"
              value={JSON.stringify(request.requestDetails, null, 2)}
              multiline
            />
          )}
          {request.rejectionReason !== null && (
            <InfoRow
              label="Motivo rejeição"
              value={request.rejectionReason}
              multiline
              highlight="danger"
            />
          )}
        </Section>

        {/* Workflow timeline */}
        <Section title="Histórico do workflow">
          <WorkflowTimeline request={request} />
        </Section>

        {/* Ações — capability-gated, derivadas do workflow real (ADR-098) */}
        {canApprove &&
          VALID_TRANSITIONS[request.status].length > 0 &&
          (() => {
            const nexts = VALID_TRANSITIONS[request.status];
            const positives = nexts.filter((s) => !NEGATIVE_TRANSITIONS.includes(s));
            const negatives = nexts.filter((s) => NEGATIVE_TRANSITIONS.includes(s));
            return (
              <Section title="Ações">
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {positives.map((next) => (
                    <button
                      key={next}
                      type="button"
                      style={btnStyle('primary')}
                      disabled={transitionMutation.isPending}
                      onClick={() => {
                        transitionMutation.mutate(
                          {
                            requestId: request.id,
                            version: request.version,
                            newStatus: next,
                            // Step 3 não-pulável (LGPD §1.2): iniciar processamento
                            // marca a revisão do DPO agora (Engine exige reviewedAt).
                            ...(next === 'in_processing'
                              ? { reviewedAt: new Date().toISOString() }
                              : {}),
                          },
                          // O drawer recebe a request como prop estática; ao concluir,
                          // fecha p/ o DPO ver a lista (refetch via invalidate) atualizada.
                          { onSuccess: onClose },
                        );
                      }}
                    >
                      {TRANSITION_LABEL[next]}
                    </button>
                  ))}

                  {pendingNegative === null &&
                    negatives.map((next) => (
                      <button
                        key={next}
                        type="button"
                        style={btnStyle('danger')}
                        onClick={() => {
                          setPendingNegative(next);
                        }}
                      >
                        {TRANSITION_LABEL[next]}
                      </button>
                    ))}

                  {pendingNegative !== null && (
                    <div
                      style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}
                    >
                      <textarea
                        value={rejectReason}
                        onChange={(e) => {
                          setRejectReason(e.target.value);
                        }}
                        placeholder={`Motivo — ${TRANSITION_LABEL[pendingNegative]} (obrigatório)`}
                        style={{
                          padding: 8,
                          borderRadius: 6,
                          border: '1px solid #d1d5db',
                          fontSize: 12,
                          resize: 'vertical',
                          minHeight: 64,
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          style={btnStyle('danger')}
                          disabled={rejectReason.trim() === '' || transitionMutation.isPending}
                          onClick={() => {
                            transitionMutation.mutate(
                              {
                                requestId: request.id,
                                version: request.version,
                                newStatus: pendingNegative,
                                rejectionReason: rejectReason.trim(),
                              },
                              { onSuccess: onClose },
                            );
                            setPendingNegative(null);
                            setRejectReason('');
                          }}
                        >
                          Confirmar
                        </button>
                        <button
                          type="button"
                          style={btnStyle('ghost')}
                          onClick={() => {
                            setPendingNegative(null);
                            setRejectReason('');
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {transitionMutation.isError && (
                  <div style={{ marginTop: 8, color: '#ef4444', fontSize: 11 }}>
                    Erro ao transitar status. Tente novamente.
                  </div>
                )}
              </Section>
            );
          })()}

        {/* Iniciar anonimização — DPO exclusivo */}
        {canExecute && request.status === 'in_processing' && (
          <Section title="Anonimização (DPO exclusivo)">
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              Ação <strong>irreversível</strong>. Requer confirmação por digitação e
              pré-visualização do impacto (3 níveis: parcial / completo / exclusão em cascata).
            </div>
            <button
              type="button"
              style={btnStyle('danger')}
              onClick={() => {
                onInitiateAnonymization(request);
              }}
            >
              ⚠ Iniciar Anonimização (Wizard)
            </button>
          </Section>
        )}
      </div>
    ),
  };

  // ─── Tab 2: Cascade ────────────────────────────────────────────────────────

  const tabCascade: DrawerTabItem = {
    id: 'cascade',
    label: 'Trilha de auditoria',
    content: (
      <div style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          Cadeia de eventos de auditoria vinculados a esta solicitação.
        </div>
        {/* D-B own gate */}
        <CapabilityGate
          capability="audit-events-read"
          fallback={
            <div style={{ padding: 16, color: '#9ca3af', fontSize: 12 }}>
              Seu perfil não tem acesso à trilha de auditoria desta solicitação (disponível a partir
              do pacote Team).
            </div>
          }
        >
          <LgpdCascadePreview
            entityId={request.id}
            entityLabel={`Solicitação ${request.id.slice(0, 12)}`}
          />
        </CapabilityGate>
      </div>
    ),
  };

  // ─── Tab 3: Documentos ─────────────────────────────────────────────────────

  const tabDocumentos: DrawerTabItem = {
    id: 'documentos',
    label: 'Documentos evidenciais',
    content: (
      <div style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          Documentos de identidade e evidências para validação do titular. O envio é guardado no
          repositório de documentos do tenant, com deduplicação automática.
        </div>
        {/* D-B own gate */}
        <CapabilityGate
          capability="lgpd-requests-approve"
          fallback={
            <div style={{ padding: 16, color: '#9ca3af', fontSize: 12 }}>
              Seu perfil não tem permissão para anexar documentos a esta solicitação.
            </div>
          }
        >
          <DocumentationGatedUploader
            requiredCapability="lgpd-requests-approve"
            hasCapability={canApprove}
            accept=".pdf,.jpg,.jpeg,.png"
            maxFileSizeBytes={10 * 1024 * 1024}
            label="Documentos evidenciais do titular"
            onFilesSelected={(_files: File[]) => {
              // P17: upload registra audit server-side via DocumentReferencesService
              // Integração real com documents/ endpoint pendente (PR-B Fatia D)
            }}
          />
        </CapabilityGate>
      </div>
    ),
  };

  // ─── Tab 4: Anonimizações ──────────────────────────────────────────────────

  const tabAnonimizacoes: DrawerTabItem = {
    id: 'anonimizacoes',
    label: 'Anonimizações',
    badgeCount: anonymizations.length,
    content: (
      <div style={{ padding: '16px 20px' }}>
        <CapabilityGate
          capability="lgpd-requests-read"
          fallback={
            <div style={{ padding: 16, color: '#9ca3af', fontSize: 12 }}>
              Seu perfil não tem acesso ao histórico de anonimizações desta solicitação.
            </div>
          }
        >
          {anonLoading && <div style={{ color: '#9ca3af', fontSize: 12 }}>Carregando...</div>}
          {!anonLoading && anonymizations.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
              Nenhuma anonimização registrada para esta solicitação.
            </div>
          )}
          {!anonLoading && anonymizations.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {anonymizations.map((anon) => (
                <AnonymizationRow key={anon.id} anon={anon} />
              ))}
            </div>
          )}
        </CapabilityGate>
      </div>
    ),
  };

  return (
    <Drawer
      isOpen={request !== null}
      onClose={onClose}
      title={`${REQUEST_TYPE_LABEL[request.requestType] ?? request.requestType} · ${request.requesterSelfDeclaredData?.name ?? 'Titular'}`}
      width="lg"
      tabs={[tabDados, tabCascade, tabDocumentos, tabAnonimizacoes]}
    />
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#374151',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 8,
          paddingBottom: 4,
          borderBottom: '1px solid #f3f4f6',
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  children,
  multiline = false,
  highlight,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
  multiline?: boolean;
  highlight?: 'danger';
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: multiline ? 'flex-start' : 'center',
        fontSize: 12,
      }}
    >
      <span
        style={{
          color: '#6b7280',
          width: 110,
          flexShrink: 0,
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children !== undefined ? (
        children
      ) : (
        <span
          style={{
            color: highlight === 'danger' ? '#b91c1c' : '#111827',
            whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </span>
      )}
    </div>
  );
}

function WorkflowTimeline({ request }: { request: LgpdRequestWireItem }): JSX.Element {
  const steps: { label: string; date: string | null; done: boolean }[] = [
    { label: 'Recebida', date: request.receivedAt, done: true },
    {
      label: 'Identidade validada',
      date: request.identityValidatedAt,
      done: request.identityValidatedAt !== null,
    },
    {
      label: 'Em processamento',
      date: request.processingStartedAt,
      done: request.processingStartedAt !== null,
    },
    { label: 'Concluída', date: request.completedAt, done: request.completedAt !== null },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {steps.map((step) => (
        <div
          key={step.label}
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}
        >
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: step.done ? '#22c55e' : '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: step.done ? '#fff' : '#9ca3af',
              fontSize: 9,
            }}
          >
            {step.done ? '✓' : '○'}
          </span>
          <span
            style={{ color: step.done ? '#111827' : '#9ca3af', fontWeight: step.done ? 500 : 400 }}
          >
            {step.label}
          </span>
          {step.date !== null && (
            <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>
              {new Date(step.date).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function AnonymizationRow({ anon }: { anon: AnonymizationWireItem }): JSX.Element {
  const statusColor =
    anon.status === 'completed'
      ? '#22c55e'
      : anon.status === 'failed'
        ? '#ef4444'
        : anon.status === 'executing'
          ? '#f59e0b'
          : '#6b7280';

  return (
    <div
      style={{
        padding: '8px 12px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        fontSize: 11,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontFamily: 'monospace', color: '#6b7280' }}>{anon.id.slice(0, 12)}</span>
        <span style={{ color: statusColor, fontWeight: 600 }}>{anon.status}</span>
        <span
          style={{
            marginLeft: 'auto',
            padding: '1px 6px',
            background: '#ede9fe',
            color: '#7c3aed',
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 10,
          }}
        >
          {anon.level}
        </span>
      </div>
      <div style={{ color: '#6b7280' }}>
        {anon.entityType} · {anon.entityId.slice(0, 12)}
      </div>
      {anon.failureReason !== null && (
        <div style={{ color: '#ef4444', marginTop: 4 }}>{anon.failureReason}</div>
      )}
    </div>
  );
}

// ─── Button styles ─────────────────────────────────────────────────────────────

function btnStyle(variant: 'primary' | 'danger' | 'ghost'): React.CSSProperties {
  if (variant === 'primary') {
    return {
      padding: '7px 16px',
      borderRadius: 6,
      border: 'none',
      background: '#6366f1',
      color: '#fff',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    };
  }
  if (variant === 'danger') {
    return {
      padding: '7px 16px',
      borderRadius: 6,
      border: 'none',
      background: '#ef4444',
      color: '#fff',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    };
  }
  return {
    padding: '7px 16px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  };
}
